import { WarningIcon } from '@chakra-ui/icons';
import {
  Button,
  FormControl,
  FormErrorMessage,
  Heading,
  HStack,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';
import React from 'react';

import { consts as commonConsts } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

export const ManualDatesComponent = ({
  title,
  dateList,
  setManualDates,
  includeBorder = true,
}: {
  title: 'Add' | 'Exclude';
  dateList: Util.FullDateStr[];
  setManualDates: (dates: Util.FullDateStr[]) => void;
  includeBorder?: boolean;
}) => {
  const [value, setValue] = React.useState<string>(dateList.join(', '));
  const dateRegex = commonConsts.regExpFullDateStr.source.slice(1, -1);
  const emptyStringRegex = new RegExp(/^\s*$/);
  const validationRegex = new RegExp(
    `^"?${dateRegex}"?\\s*(,\\s*"?${dateRegex}"?)*\\s*,*\\s*$`,
  );
  const getDatesRegex = new RegExp(dateRegex, 'g');
  const [isError, setIsError] = React.useState<boolean>(false);
  const errorMsg =
    'Incorrect Date Format. Use `YYYY-MM-DD, YYYY-MM-DD, YYYY-MM-DD` format.';

  return (
    <Stack
      border={includeBorder ? '1px solid' : 'inherit'}
      borderColor={includeBorder ? 'gray.300' : 'inherit'}
      borderRadius="8px"
      padding="20px"
      spacing="25px"
    >
      <Heading as="h4" size="md">
        {title} Manual Dates
      </Heading>
      <FormControl isInvalid={isError}>
        <Stack spacing="20px">
          <HStack spacing="30px">
            <Text fontSize="md">Dates</Text>
            <Input
              value={value}
              type="text"
              placeholder="Enter Dates (`YYYY-MM-DD, YYYY-MM-DD, YYYY-MM-DD`)"
              borderRadius="8px"
              onChange={(e) => setValue(e.target.value)}
              onBlur={() => {
                setIsError(
                  !value.match(emptyStringRegex) &&
                    !value.match(validationRegex),
                );
              }}
            />
          </HStack>
          {isError && (
            <HStack
              spacing="8px"
              bg="red.100"
              borderRadius="8px"
              padding="15px"
            >
              <WarningIcon color="red.500" />
              <FormErrorMessage>{errorMsg}</FormErrorMessage>
            </HStack>
          )}
          <HStack spacing="30px">
            <Spacer />
            <Button
              isDisabled={
                !value.match(validationRegex) && !value.match(emptyStringRegex)
              }
              onClick={() => {
                const dateMatches = value.match(getDatesRegex);
                if (dateMatches) {
                  setManualDates(dateMatches as Util.FullDateStr[]);
                }
                if (value.match(emptyStringRegex)) {
                  setManualDates([]);
                }
              }}
            >
              Save Manual Dates
            </Button>
          </HStack>
        </Stack>
      </FormControl>
    </Stack>
  );
};

export const ManualDatesModal = ({
  title,
  dateList,
  setManualDates,
  isOpen,
  onClose,
}: {
  title: 'Add' | 'Exclude';
  dateList: Util.FullDateStr[];
  setManualDates: (dates: Util.FullDateStr[]) => void;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader />
        <ModalCloseButton />
        <ManualDatesComponent
          title={title}
          dateList={dateList}
          setManualDates={(dates) => {
            setManualDates(dates);

            onClose();
          }}
          includeBorder={false}
        />
      </ModalContent>
    </Modal>
  );
};
